import { FolderLook } from '@idc-forms/client-app-domain/documents/folder-look'
import {
  Download,
  Eye,
  EyeOff,
  FolderInput,
  Lock,
  MoreHorizontal,
  PencilLine,
  Trash2
} from 'lucide-react'
import { memo } from 'react'
import { CapDocumentCard } from '@/features/client-app-preview/components/cap-document-card'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/shared/ui/dropdown-menu'
import { INHERITED_ORIGIN_LABEL } from '../domain/inherited-documents'
import { useDocumentThumbnailQuery } from '../hooks/use-owner-documents'
import type { CatalogOwnerType } from '../models/document-owner'

/** El valor del ítem «sin categoría» en el submenú. No puede ser `''`: Radix lo reserva. */
const NO_CATEGORY = '__none__'

/**
 * Lo que la tarjeta necesita saber del documento, venga de lo propio o de lo heredado.
 *
 * Es un tipo propio y no el DTO: las dos listas publican formas distintas —lo heredado no tiene
 * `slot` ni `comment`— y lo que la tarjeta dibuja es la intersección. Declararla acá es lo que
 * permite que la MISMA tarjeta sirva a las dos secciones sin un ternario por campo.
 */
export type PortfolioCardDocument = {
  id: string
  name: string
  mimeType: string | null
  pages: number | null
  hasThumbnail: boolean
  thumbnailAspect: number | null
  visibility: 'public' | 'internal'
  categoryId: string | null
}

export type PortfolioDocumentCardProps = {
  document: PortfolioCardDocument
  /** La pinta de la categoría que lo contiene: decide el arte cuando no hay miniatura. */
  look?: FolderLook
  /**
   * El origen, sólo en lo heredado. Su presencia es lo que vuelve la tarjeta de SÓLO LECTURA:
   * no hay menú, no hay publicar. No es una prop `readOnly` aparte porque serían dos banderas
   * que pueden contradecirse, y la única lectura sin acciones es justamente la heredada.
   */
  originType?: CatalogOwnerType
  canPublish?: boolean
  /** La plantilla global, ya sin las deprecadas, para el submenú de reclasificar. */
  categories?: Array<{ id: string; name: string }>
  pending?: boolean
  onOpen: () => void
  onToggleVisibility?: (next: 'public' | 'internal') => void
  onRename?: () => void
  onChangeCategory?: (categoryId: string | null) => void
  onDownload?: () => void
  onDelete?: () => void
}

/**
 * UNA TARJETA DEL CARTAPACIO: el dibujo de la app, con la capa de edición del back-office encima.
 *
 * ## La composición, y por qué el chrome es HERMANO y no hijo
 *
 * `CapDocumentCard` es un `<button>`: toda la tarjeta abre el documento, igual que en el teléfono.
 * Meterle adentro el menú «⋯» sería anidar un botón dentro de otro —HTML inválido, y en la
 * práctica un click que dispara las dos cosas—. Así que el chrome se apila ENCIMA como hermano,
 * dentro de un contenedor posicionado. La tarjeta no se entera de que existe, que es exactamente
 * la condición que el ADR 0039 pide: componer y envolver, nunca modificar.
 *
 * ## El estado se dice SIEMPRE, con forma y con palabra
 *
 * Un chip en la esquina superior izquierda, en las doce tarjetas, en la misma posición. No
 * aparece al pasar el mouse: en una grilla de doce, «cuáles ve el cliente» es la pregunta que se
 * responde de un vistazo, y una señal que hay que ir a buscar tarjeta por tarjeta no la responde.
 * Y se dice dos veces —ícono y rótulo— porque confundir «interno» con «publicado» es creer que un
 * documento salió cuando no, o que no salió cuando ya está en el teléfono del cliente.
 *
 * ## Lo heredado no tiene menú, y esa ausencia es la explicación
 *
 * Se ve igual —es el mismo documento— pero sin una sola acción y con el chip del nivel de origen.
 * Es la escalera del ADR 0023 dibujada: ver no pide permiso, editar el origen se hace en el
 * origen. Un menú deshabilitado con un tooltip diría lo mismo con más ruido y dejaría creyendo
 * que falta un permiso, cuando lo que falta es estar parado en otro nivel.
 *
 * ## La miniatura se pide sólo si existe
 *
 * `hasThumbnail` viene del listado, en la misma consulta que las N filas. Sin esa compuerta un
 * cartapacio de cuarenta PDFs anteriores al generador dispararía cuarenta llamadas para que las
 * cuarenta contesten «no tiene» — y el arte por tipo de archivo, que es lo que se dibuja
 * entonces, ya se sabe dibujar sin preguntar nada.
 */
export const PortfolioDocumentCard = memo(function PortfolioDocumentCard({
  document,
  look = FolderLook.generic,
  originType,
  canPublish = false,
  categories = [],
  pending = false,
  onOpen,
  onToggleVisibility,
  onRename,
  onChangeCategory,
  onDownload,
  onDelete
}: PortfolioDocumentCardProps) {
  const thumbnail = useDocumentThumbnailQuery(document.id, document.hasThumbnail)
  const isPublic = document.visibility === 'public'
  const inherited = Boolean(originType)

  return (
    /*
     * `data-slot` + `data-document-id` en el CONTENEDOR, no en la tarjeta.
     *
     * Es el punto de anclaje de todo lo que rodea al documento —el chip de estado, el botón de
     * publicar, el menú «⋯»— que son HERMANOS de la tarjeta y no hijos: `CapDocumentCard` es un
     * `<button>` y anidar un botón dentro de otro es HTML inválido. Sin un gancho acá, un test
     * tiene que adivinar la forma del DOM, y esa adivinanza se rompe con cada refactor sin que
     * nadie lo note hasta que la aserción deja de probar lo que decía.
     */
    <div
      data-slot="portfolio-card"
      data-document-id={document.id}
      className={cn('relative', pending && 'pointer-events-none opacity-60')}
    >
      <CapDocumentCard
        document={{
          id: document.id,
          name: document.name,
          mimeType: document.mimeType,
          pages: document.pages,
          // Las hojas firmadas llegan después que la tarjeta: hasta entonces `CapDocumentCard`
          // dibuja el arte por tipo, que es la misma caja y el mismo tamaño. Por eso no hay
          // esqueleto propio — no hay salto de layout que tapar.
          thumbnailUrls: thumbnail.data?.urls ?? [],
          thumbnailAspect: document.thumbnailAspect
        }}
        look={look}
        onSelect={onOpen}
      />

      {/*
       * El chip de estado, o el de origen si es heredado. Nunca los dos: en una tarjeta
       * heredada la visibilidad que importa ya está implícita —sólo llega lo publicado— y
       * mostrar «Publicado» ahí invitaría a buscar el botón para despublicarlo, que no está acá.
       */}
      <span
        className={cn(
          'pointer-events-none absolute top-1.5 left-1.5 z-20 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium backdrop-blur-sm',
          inherited
            ? 'bg-background/85 text-muted-foreground'
            : isPublic
              ? 'bg-emerald-600/90 text-white'
              : 'bg-background/85 text-foreground'
        )}
      >
        {inherited ? (
          INHERITED_ORIGIN_LABEL[originType as CatalogOwnerType]
        ) : (
          <>
            {isPublic ? (
              <Eye className="h-2.5 w-2.5" aria-hidden />
            ) : (
              <Lock className="h-2.5 w-2.5" aria-hidden />
            )}
            {isPublic ? 'Publicado' : 'Interno'}
          </>
        )}
      </span>

      {inherited ? null : (
        <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1">
          {canPublish ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              // `active:scale-97` y no un hover llamativo: el botón vive sobre una miniatura y
              // tiene que confirmar que escuchó sin robarle la mirada a la foto.
              className="bg-background/85 backdrop-blur-sm transition-transform duration-150 ease-out active:scale-[0.97]"
              aria-label={isPublic ? `Despublicar ${document.name}` : `Publicar ${document.name}`}
              title={isPublic ? 'Despublicar' : 'Publicar'}
              onClick={() => {
                onToggleVisibility?.(isPublic ? 'internal' : 'public')
              }}
            >
              {isPublic ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                className="bg-background/85 backdrop-blur-sm transition-transform duration-150 ease-out active:scale-[0.97]"
                aria-label={`Acciones de ${document.name}`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            {/*
             * `align="end"`: el menú se despliega desde su disparador, que está pegado al borde
             * derecho de una tarjeta de ~140 px dentro de un panel de 320. Centrado se saldría
             * del panel por la derecha.
             */}
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onSelect={() => onRename?.()}>
                <PencilLine className="h-4 w-4" />
                Cambiar nombre
              </DropdownMenuItem>

              {onChangeCategory ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <FolderInput className="h-4 w-4" />
                    Cambiar categoría
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-72 w-56 overflow-y-auto">
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                      Es el grupo con el que el cliente lo ve en su app.
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={document.categoryId ?? NO_CATEGORY}
                      onValueChange={(value) => {
                        onChangeCategory(value === NO_CATEGORY ? null : value)
                      }}
                    >
                      {/* Se ofrece explícitamente: no clasificar es una elección válida, y
                          esconderla haría que quien no sabe dónde va el archivo lo deje donde
                          estaba. */}
                      <DropdownMenuRadioItem value={NO_CATEGORY}>
                        Sin categoría
                      </DropdownMenuRadioItem>
                      {categories.map((category) => (
                        <DropdownMenuRadioItem key={category.id} value={category.id}>
                          {category.name}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : null}

              <DropdownMenuItem onSelect={() => onDownload?.()}>
                <Download className="h-4 w-4" />
                Descargar
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onSelect={() => {
                  onDelete?.()
                }}
              >
                <Trash2 className="h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
})
